<template>
  <div class="adviser-biography">
    <div
      class="adviser-biography__cta adviser-biography__cta-bio"
      :class="!showBiography ? 'adviser-biography__cta-bio--hidden' : null"
      @click="openBiographyModal(showBiography, false)"
    >
      <div>
        <span>{{ $t('appointment_form_page.biography.cta') }}</span>
        <AppIcon icon="biography" class="align-self-center" />
      </div>
    </div>
    <div
      v-if="hasEnabledReviews"
      class="adviser-biography__cta adviser-biography__cta-reviews"
      :class="!showReviews ? 'adviser-biography__cta-reviews--hidden' : null"
      @click="openBiographyModal(showReviews, true)"
    >
      <div>
        <span v-dompurify-html="$t('appointment_form_page.reviews.cta')"> </span>
        <AppIcon icon="reviews" class="align-self-center" />
      </div>
    </div>
    <Teleport to="body" v-if="showBiography || showReviews">
      <ModalWindow :show-modal="showModal" @close="closeModal" class="adviser-biography-modal">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-body overflow-scroll">
              <span class="btn-close btn-close-white" aria-label="Close" @click="closeModal">
                <AppIcon icon="close" />
              </span>
              <div ref="adviserBiographyContent" :class="adviserBiographyContentClass">
                <div class="adviser-biography-content__img">
                  <div v-if="hasVideo" class="adviser-biography-content__video">
                    <CustomVideoPlayer
                      :src="videoUrl"
                      :width="adviser.videos.width"
                      :height="adviser.videos.height"
                      preload="metadata"
                      :poster="adviserImage.url"
                      @play="updateGrid(true)"
                      @ended="updateGrid(false)"
                      @fullscreen-change="handleSafariWeirdBehavior($event)"
                    />
                  </div>
                  <AsyncImage
                    v-else
                    :image="adviserImage"
                    :show-images="showImages"
                    :current-image-index-to-load="1"
                    @load-next="loadNext()"
                    @is-ready="toggleReady(1)"
                  />
                </div>
                <div class="adviser-biography-content__name-group">
                  <span>
                    {{ formattedName }}
                  </span>
                </div>
                <div class="adviser-biography-content__languages">
                  <AdviserSpokenLanguages :adviser-spoken-languages="adviser.spoken_languages" />
                </div>
                <template v-if="showBiography && biography">
                  <hr
                    class="adviser-biography-content__separator adviser-biography-content__separator--biography"
                  />
                  <div class="adviser-biography-content__biography">
                    <span>{{ $t('appointment_form_page.biography.cta') }}</span>
                    <div v-dompurify-html="biography" />
                  </div>
                </template>
                <template v-if="showReviews">
                  <hr
                    class="adviser-biography-content__separator adviser-biography-content__separator--reviews"
                  />
                  <div class="adviser-biography-content__reviews" ref="reviewsContainer">
                    <span v-if="typeof reviewsTitle === 'string'" v-dompurify-html="reviewsTitle" />
                    <span v-else v-dompurify-html="reviewsTitle.value" />

                    <div>
                      <div
                        v-for="(review, index) in reviews"
                        :key="index"
                        class="adviser-biography-content__review"
                      >
                        <div class="adviser-biography-content-review__rating">
                          <AppIcon v-for="value in review.rating" :key="value" icon="star" />
                        </div>
                        <div
                          class="adviser-biography-content-review__text"
                          v-dompurify-html="review.review"
                        />
                        <div class="d-flex">
                          <div class="d-flex flex-column">
                            <div class="adviser-biography-content-review__name">
                              {{ review.customer_full_name }}
                            </div>
                            <div
                              class="adviser-biography-content-review__date"
                              v-show="review.date && review.date.length > 0"
                            >
                              {{ review.date }}
                            </div>
                          </div>
                          <div class="adviser-biography-content-review__quote">
                            <AppIcon icon="quote" size="2xl" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </div>
            <div class="modal-footer" :class="{ 'modal-footer--selected': isSelected }">
              <div @click="selectAdviser()">
                <span v-if="isSelected">{{ $t('appointment_form_page.biography.selected') }}</span>
                <span v-else>{{ $t('appointment_form_page.biography.select') }}</span>
                <AppIcon icon="check" />
              </div>
            </div>
          </div>
        </div>
      </ModalWindow>
    </Teleport>
  </div>
</template>

<script lang="ts">
import ModalWindow from '~conciergeUiCommons/components/ModalWindow.vue'
import { ComputedRef, defineComponent, PropType, ref } from 'vue'
import { Adviser, AdviserReview, LocalizedAdviser } from '~concierge/types/AppointmentTypes'
import AdviserSpokenLanguages from '~conciergeUiCommons/components/AdviserSpokenLanguages.vue'
import AsyncImage from '~conciergeUiCommons/components/AsyncImage.vue'
import { AsyncImageData, useShowImage } from '~conciergeUiCommons/composables/showImage'
import { useIdentityStore } from '~conciergeUiCommons/store/identityStore'
import { getActiveLanguage, trans } from 'laravel-vue-i18n'
import CustomVideoPlayer from '~conciergeUiCommons/components/CustomVideoPlayer.vue'
import { wTransChoice } from 'laravel-vue-i18n'
import AppIcon from './AppIcon.vue'
import { IdentitySettings } from '~concierge/types/IdentityTypes'

export default defineComponent({
  name: 'AdviserBiographyModal',
  components: { ModalWindow, AdviserSpokenLanguages, AsyncImage, CustomVideoPlayer },
  props: {
    adviser: {
      type: Object as PropType<Adviser>,
      required: true
    },
    isSelected: {
      type: Boolean,
      required: true
    },
    lang: {
      type: String as PropType<string>,
      default: getActiveLanguage()
    }
  },
  emits: ['select'],
  setup(props, { emit }) {
    const { toggleReady, loadNext, showImages } = useShowImage(1)
    const select = () => {
      emit('select', props.adviser)
    }

    const identityStore = useIdentityStore()
    const settings = identityStore.identity?.settings as IdentitySettings
    const showSpokenLanguages = settings?.adviserSpokenLanguageEnabled ?? false
    const hasEnabledReviews = settings?.reviewEnabled ?? false
    const showModal = ref<boolean>(false)

    const reviewsContainer = ref<HTMLElement | null>(null)

    return {
      select,
      showModal,
      toggleReady,
      loadNext,
      showImages,
      showSpokenLanguages,
      hasEnabledReviews,
      reviewsContainer
    }
  },
  data() {
    return {
      adviserBiographyContentClass: 'adviser-biography-content'
    }
  },
  computed: {
    hasVideo(): boolean {
      return (
        this.adviser.videos[this.lang as keyof LocalizedAdviser] !== undefined &&
        this.adviser.videos[this.lang as keyof LocalizedAdviser] !== ''
      )
    },
    hasReviews(): boolean {
      return (
        Array.isArray(this.adviser.reviews[this.lang as keyof LocalizedAdviser]) &&
        this.adviser.reviews[this.lang as keyof LocalizedAdviser].length > 0
      )
    },
    reviews(): AdviserReview[] {
      return this.adviser.reviews[this.lang as keyof LocalizedAdviser] ?? []
    },
    videoUrl(): string {
      return this.adviser.videos[this.lang as keyof LocalizedAdviser] ?? ''
    },
    biography(): string {
      return this.adviser.biography?.[this.lang as keyof LocalizedAdviser] ?? ''
    },
    showBiography(): boolean {
      return typeof this.biography === 'string' && this.biography.length > 0
    },
    showReviews(): boolean {
      return this.hasEnabledReviews && this.hasReviews
    },
    reviewsTitle(): string {
      if (!this.showReviews) {
        return this.$t('appointment_form_page.reviews.cta')
      }
      return this.$t('appointment_form_page.reviews.title')
    },
    adviserImage(): AsyncImageData {
      return {
        id: this.adviser.id,
        index: 1,
        url: this.adviser.photo_large?.url ?? '',
        width: this.adviser.photo_large?.width ?? null,
        height: this.adviser.photo_large?.height ?? null,
        alt: this.adviser?.first_name ?? 'Photo du conseiller',
        ready: false,
        show: false
      }
    },
    formattedName(): string {
      return this.adviser.name.replace(/-/g, '\u2011')
    }
  },
  methods: {
    closeModal(): void {
      this.showModal = false
      this.updateGrid(false)
    },
    selectAdviser(): void {
      this.$emit('select', this.adviser)
      this.showModal = false
    },
    updateGrid(isPlaying: boolean): void {
      if (isPlaying) {
        this.adviserBiographyContentClass =
          'adviser-biography-content adviser-biography-content--playing-video'
      } else {
        this.adviserBiographyContentClass = 'adviser-biography-content'
      }
    },
    openBiographyModal(showModule: boolean, gotToReviews: boolean): void {
      if (showModule) {
        this.showModal = true
        if (gotToReviews) {
          this.$nextTick(() => {
            const reviewsContainer = this.$refs.reviewsContainer as HTMLElement
            if (reviewsContainer) {
              reviewsContainer.scrollIntoView({ behavior: 'smooth' })
            }
          })
        }
      }
    },
    handleSafariWeirdBehavior(isEnteringToFullscreen: boolean): void {
      if (!isEnteringToFullscreen) {
        const container = this.$refs.adviserBiographyContent as HTMLElement
        if (container) {
          container.style.display = 'block'
          this.$nextTick(() => {
            this.adviserBiographyContentClass =
              'adviser-biography-content adviser-biography-content--playing-video'
          })
        }
      }
    }
  }
})
</script>
